import { NgRedux } from '@ptl/angular-redux-store';
import { KeyValue } from '@angular/common';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Component, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { BrandingConfigurationService } from '@ptl/brand';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { StateStructure } from '@ptl/state';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { PlansStateStructure } from 'src/app/angular-elements/plan-selection/configs/interfaces/plansStateStructure.interface';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { Product } from 'src/app/angular-elements/plan-selection/interfaces/product.interface';
import { PlanUtilityService } from 'src/app/angular-elements/plan-selection/plan-summary-list/plan-utility.service';
import { EffectiveDateStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/effective-date.state-structure.interface';
import { PrePopulatedDataStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/prePopulatedDataStateStructure.interface';
import { DateService } from 'src/app/shared/services/date.service';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { CustomCategoryMap, CustomDefaultCheckedMap } from './interfaces/category-map.interface';
import { EffectiveDateComplexQuestion } from './interfaces/effective-date-complex-question.interface';
import { ElectionQuestionsServiceResponse } from './interfaces/election-questions-service-response.interface';
import { FormValuesAsJson } from './interfaces/form-values-as-json.interface';
import { PlanBasedQuestionsOutput } from './interfaces/plan-based-questions-output.interface';
import { MatDialog } from '@angular/material/dialog';
import { ConflictingEffectiveDateModalComponent } from './conflicting-effective-date-modal/conflicting-effective-date-modal.component';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { ElectionInputStructure } from 'src/app/angular-elements/shared/interfaces/electionInputStructure.interface';
import { saveElectionAppIn } from 'src/app/shared/reducers/enrollmentApp.reducer';
import { setSelectedElectionPeriodEvent } from 'src/app/shared/reducers/election-period.reducer';

@FormComponentLookup('effective-date-attestation')
@Component({
    selector: 'app-effective-date-attestation',
    templateUrl: './effective-date-attestation.component.html',
    styleUrls: ['./effective-date-attestation.component.scss'],
    standalone: false
})
export class EffectiveDateAttestationComponent extends FormEntryComponentClass implements OnDestroy {

  electionQuestionsForm: UntypedFormGroup;
  formChanges: Subscription;
  isNewInd: boolean;
  displaySpinner = false;
  categoryMappings: CustomCategoryMap;
  allQuestionsConcat: number[] = [];
  questionsResponse: PlanBasedQuestionsOutput[];
  questionRadioFlag: boolean;
  questionRadio: string;
  questionRadioOptions: string[];
  questionDateFlag: boolean;
  questionDate: string;
  displayDateSelectionSection: boolean = false;
  defaultChecked: CustomDefaultCheckedMap;
  startOfNextMonth: string;

  index = 0;
  tempOptionList;

  electionPeriodSelectionMessage = "";
  enrollmentPeriod: boolean = false;
  effectiveDatePossibilities: any[];
  noEffectiveDateReturned: boolean;

  minDate = "1900-01-01";
  maxDate = "2200-01-01";

  effectiveDateRadioForm: UntypedFormGroup;
  effectiveDateRadioFormChanges: Subscription;

  allowProceed: boolean;

  // Order by ascending property value
  valueAscOrder = (a: KeyValue<any, any>, b: KeyValue<any, any>): number => {
    const aOrder = this.categoryMappings[a.key].order;
    const bOrder = this.categoryMappings[b.key].order;
    return aOrder > bOrder ? 1 : (bOrder > aOrder ? -1 : 0);
  }

  questionInOrder = (a: KeyValue<any, any>, b: KeyValue<any, any>): number => {
    let aOrder = this.allQuestionsConcat.indexOf(Number(a.key));
    let bOrder = this.allQuestionsConcat.indexOf(Number(b.key));
    if (aOrder === -1) { aOrder = 1000 };
    if (bOrder === -1) { bOrder = 1000 };
    return aOrder > bOrder ? 1 : (bOrder > aOrder ? -1 : 0);
  }


  constructor(
    private dateUtilityService: DateService,
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService,
    private reducerUtilService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    protected ngRedux: NgRedux<StateStructure>,
    private formBuilder: UntypedFormBuilder,
    private commonUtilityService: CommonUtilityService,
    private matDialog: MatDialog
  ) {
    super();
    this.effectiveDateRadioForm = this.formBuilder.group({
      'effectiveDate': this.formBuilder.control('')
    });
  }

  async ngOnInit(): Promise<void> {
    this.displaySpinner = true;

    await this.setValuesForCoverageValidation();

    this.effectiveDateRadioForm.get('effectiveDate').setErrors({ 'incorrect': true });

    this.effectiveDateRadioFormChanges = this.effectiveDateRadioForm.valueChanges.subscribe(formValuesAsJson => {
      const dynamicObject = {};
      dynamicObject[this.entry.formControlAttributes.formControlName] = formValuesAsJson.effectiveDate?.key;
      setTimeout(() => { this.form.patchValue(dynamicObject); }, 0);
      this.ngRedux.dispatch(setSelectedElectionPeriodEvent(formValuesAsJson.effectiveDate));
    })

    this.formChanges = this.electionQuestionsForm.valueChanges.subscribe(formValuesAsJson => {
      Object.keys(formValuesAsJson).forEach((categoryName) => {
        Object.keys(formValuesAsJson[categoryName]).forEach((question) => {
          let ques = formValuesAsJson[categoryName][question];
          if (ques && ques.hasOwnProperty("eventValue")) {
            if (ques.eventValue) {
              if (ques.hasOwnProperty("date")) {
                let quesDate = this.electionQuestionsForm.get(categoryName).get(question).get("date");
                quesDate.setValidators([Validators.required]);
              }
              if (ques.hasOwnProperty("radio")) {
                let quesRadio = this.electionQuestionsForm.get(categoryName).get(question).get("radio");
                quesRadio.setValidators([Validators.required]);
              }
            } 
            else {
              if (ques.hasOwnProperty("date")) {
                this.electionQuestionsForm.get(categoryName).get(question).get("date").clearValidators();
                this.electionQuestionsForm.get(categoryName).get(question).get("date").updateValueAndValidity({ emitEvent: false });
              }
              if (ques.hasOwnProperty("radio")) {
                this.electionQuestionsForm.get(categoryName).get(question).get("radio").clearValidators();
                this.electionQuestionsForm.get(categoryName).get(question).get("radio").updateValueAndValidity({ emitEvent: false });
              }
            }
          }
        });
      });
    });
  }

  ngOnDestroy(): void {
    if (this.formChanges) { this.formChanges.unsubscribe(); }
    if (this.effectiveDateRadioFormChanges) { this.effectiveDateRadioFormChanges.unsubscribe(); }
  }

  async setValuesForCoverageValidation() {

    const planState: PlansStateStructure = this.reducerUtilService.getState('Plans');
    const plansFromState = this.planUtilityService.getPlansByProductGroupAsArray();
    let selectedPlan: Product<PlanAttributes, SummaryView>;

    try {
      selectedPlan =
        plansFromState.filter(plan => plan.attributes.PLAN_ID === planState.planIdForEnrollment)[0];
    } catch (e) {
      console.error('No plan selected: ', e);
    }

    const planInfo: PlansStateStructure = this.reducerUtilService.getState('Plans');
    const productData = {
      planId: planInfo.planIdForEnrollment,
      planName: selectedPlan && selectedPlan.attributes && selectedPlan.attributes.PRODUCT_NAME,
      cmsPlanID: selectedPlan && selectedPlan.attributes && selectedPlan.attributes.CMS_PLAN_ID,
      pbpId: selectedPlan && selectedPlan.attributes && selectedPlan.attributes.PBP_ID
    };

    const partAEffDate: string = this.dateUtilityService.getNullOrFormattedDate(this.reducerUtilService.getPayloadState('enrollment.partAEffectiveDate'), 'MM/DD/YYYY');
    const partBEffDate: string = this.dateUtilityService.getNullOrFormattedDate(this.reducerUtilService.getPayloadState('enrollment.partBEffectiveDate'), 'MM/DD/YYYY');
    const receivedDate = this.dateUtilityService.getTodayAsFormattedString('MM/DD/YYYY');
    const appStates = this.reducerUtilService.getApplicationStates();
    const effectiveDate = appStates.EffectiveDate as EffectiveDateStateStructure;
    const selectedYear = effectiveDate.effectiveDateYear;

    let dob = this.reducerUtilService.getPayloadState('personalInfo.dob');
    const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilService.getState('PrePopulateData');
    if (!dob) {
      dob = prePopulateData.prepopulatedFields.dob;
    }

    const coverageValidationResponse = await this.initialCoverageValidation(
      partAEffDate, partBEffDate, receivedDate, selectedYear, dob.toString()
    );

    if (coverageValidationResponse instanceof HttpErrorResponse) {
      this.setEffectiveDateOnServiceFailure();
      this.displayDateSelectionSection = true;

    } else if (coverageValidationResponse) {
      this.isNewInd = coverageValidationResponse.payload.INDICATOR;
    }

    const planId = productData.cmsPlanID;
    const pbp = productData.pbpId;

    const response = await this.getPlanBasedElectionQues(planId, pbp, receivedDate, selectedYear);

    if (response instanceof HttpErrorResponse) {
      this.setEffectiveDateOnServiceFailure();
      this.displayDateSelectionSection = true;

    } else if (response) {
      this.displaySpinner = false;
      this.generateContentFromResponse(response.payload.planBasedQuestionsOutput);
      this.generateFormFromResponse(response.payload.planBasedQuestionsOutput, coverageValidationResponse);
    }
  }

  getMatchedQuestionById(id: string): PlanBasedQuestionsOutput {

    /** Get all questions with matcing Id */
    const filteredMatchedQuestions = this.questionsResponse.filter((item, index) => {
      return item.QUESTIONNUMBER === id;
    });

    /** Get the first matched question */
    const matchedQues = filteredMatchedQuestions && filteredMatchedQuestions[0];

    return matchedQues;
  };

  getContent(type: 'category' | 'question', id: string) {
    if (type === 'question') {
      /** If question was matched - return its description */
      const matchedQuestionById = this.getMatchedQuestionById(id);
      if (matchedQuestionById) {
        return matchedQuestionById.QUESTIONDESCRIPTION || 'Question Description Missing from Response';
      }
    }

    if (type === 'category') {
      return this.categoryMappings[id].displayName || 'A category with no name';
    }
  }

  checkForRadio(questionId: string): boolean {
    const matchedQuestionById = this.getMatchedQuestionById(questionId);
    if (matchedQuestionById && matchedQuestionById.QUESTIONRADIOFLAG) {
      this.questionRadioFlag = true;
      this.questionRadio = matchedQuestionById.QUESTIONRADIOTEXT;
      this.questionRadioOptions = matchedQuestionById.QUESTIONRADIOOPTIONS;
      this.tempOptionList = this.questionRadioOptions[this.index];
      return matchedQuestionById.QUESTIONRADIOFLAG;
    }
  }

  getRadioOptionsById(questionId: string): string[] {
    const matchedQuestionById = this.getMatchedQuestionById(questionId);
    let radioOptionsExist = matchedQuestionById && matchedQuestionById.QUESTIONRADIOFLAG && matchedQuestionById.QUESTIONRADIOOPTIONS;
    if (radioOptionsExist) {
      this.questionRadioOptions = matchedQuestionById.QUESTIONRADIOOPTIONS;
      return matchedQuestionById.QUESTIONRADIOOPTIONS;
    }
  }

  checkForDate(questionId: string): boolean {
    const matchedQuestionById = this.getMatchedQuestionById(questionId);
    if (matchedQuestionById && matchedQuestionById.QUESTIONINSERTDATEFLAG) {
      this.questionDateFlag = true;
      this.questionDate = matchedQuestionById.QUESTIONDATELABEL;
      return matchedQuestionById.QUESTIONINSERTDATEFLAG;
    }
  }

  generateContentFromResponse(questionsArray: PlanBasedQuestionsOutput[]) {
    this.questionsResponse = questionsArray;
  }

  generateFormFromResponse(questionsArray: PlanBasedQuestionsOutput[], newToMedicareIndicator: boolean) {

    this.electionQuestionsForm = this.formBuilder.group({});

    this.categoryMappings = this.entry.custom.categoryMappings;
    this.defaultChecked = this.entry.custom.defaultChecked;


    Object.keys(this.categoryMappings).forEach(categoryName => {

      this.allQuestionsConcat = this.allQuestionsConcat.concat(this.categoryMappings[categoryName].questionsById);

      const group = this.formBuilder.group({});

      questionsArray.forEach(question => {
        if (this.categoryMappings[categoryName].questionsById.includes(parseInt(question.QUESTIONNUMBER))) {
          /**
           * Returns a control if there are no 'radio' or 'date' sub-questions
           * Returns a group, which houses value and sub-question controls
           * */
          const addQuestion = (id: string) => {
            return {
              flat: () => {
                return this.formBuilder.control(null);
              },
              sub: (hasRadio: boolean, hasDate: boolean) => {
                let group = this.formBuilder.group({});

                group.addControl('eventValue', this.formBuilder.control(null));
                if (hasRadio) { group.addControl('radio', this.formBuilder.control(null)) }
                if (hasDate) { group.addControl('date', this.formBuilder.control(null)) }

                return group;
              }
            }
          }

          /** Strategy */
          const buildQuestion = (id: string): UntypedFormControl | UntypedFormGroup => {

            const hasRadio = this.checkForRadio(id);
            const hasDate = this.checkForDate(id);

            if (hasRadio || hasDate) {
              return addQuestion(id).sub(hasRadio, hasDate);
            } else {
              return addQuestion(id).flat();
            }

          }

          const control: AbstractControl = buildQuestion(question.QUESTIONNUMBER);
          let questionNumberAsNumber = Number(question.QUESTIONNUMBER);
          if (newToMedicareIndicator && this.defaultChecked.questionsById.includes(questionNumberAsNumber)) {
            control.setValue(true);
          }

          /** Ad control to group */
          group.addControl(
            question.QUESTIONNUMBER,
            control
          );
          /** Add Control */
          // group.addControl(question.QUESTIONNUMBER, this.formBuilder.control(null));


        }
      });

      /** Add category form group, named after the custom mapping */
      this.electionQuestionsForm.addControl(categoryName, group);
    });

  }

  getPlanBasedElectionQues(planId: string, pbp: string, receiveDate: string, selectedYear: string): Promise<ElectionQuestionsServiceResponse> {

    const payload = {
      payload: {
        PLANID: planId,
        PBP: pbp,
        RECEIVEDDATE: this.dateUtilityService.getDateAsFormattedString(receiveDate, 'MM/DD/YYYY'),
        SELECTEDYEAR: selectedYear
      }
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      // url: '/assets/mock-data/PlanBasedElectionQues.json',
      url: '/cmcsnr/validateElection/planBasedElectionQuestions',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        'x-hmhs-test-backend': environment.external.engen.eBill.overrideEnv
      }),
      POST_OR_PUT_PAYLOAD: payload
    });


    this.httpClient.setStrategy(XHR_STRATEGIES.WAS_INTER);
    return this.httpClient.makeRequest<ElectionQuestionsServiceResponse>(requestConfig);
  }

  async initialCoverageValidation(partAEffDate: string, partBEffDate: string, receiveDate: string, selYear: string, dob: string): Promise<any> {
    const payload = {
      payload: {
        PARTAEFFDATE: partAEffDate,
        PARTBEFFDATE: partBEffDate,
        RECEIVEDDATE: receiveDate,
        SELECTEDYEAR: selYear,
        BIRTHDATE: this.dateUtilityService.getDateAsFormattedString(dob, 'MM/DD/YYYY')
      }
    };

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/cmcsnr/validateElection/initialCoverageValidation',
      headers: new HttpHeaders({
        'x-hmhs-test-backend': environment.external.engen.eBill.overrideEnv
      }),
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.WAS_INTER);
    return this.httpClient.makeRequest(requestConfig);
  }

  async addPostElectionQuestionValidatedQuestions(form: UntypedFormGroup) {

    /** Questions from Form */
    const val: FormValuesAsJson = form.value;
    let inputArray = [];
    Object.keys(val).forEach(key => {
      Object.keys(val[key]).forEach(questionKey => {
        let question: boolean | EffectiveDateComplexQuestion = val[key][questionKey];
        let checked: boolean;

        // Complex object
        if (question && question.hasOwnProperty('eventValue')) {
          const complexQuestion = question as EffectiveDateComplexQuestion;
          const controlValue = complexQuestion.eventValue;
          checked = controlValue;
        } else {
          // Regular checkbox
          const simpleQuestion = question as boolean;
          checked = simpleQuestion;
        }

        if (checked) {
          inputArray = inputArray.concat(this.questionsResponse.filter(question => question.QUESTIONNUMBER === questionKey));
        }
      })
    });

    if (!inputArray.length) {
      return;
    }

    /** Plan Data from State */
    const planInfo: PlansStateStructure = this.reducerUtilService.getState('Plans');
    const plansFromState = this.planUtilityService.getPlansByProductGroupAsArray();
    let selectedPlan: Product<PlanAttributes, SummaryView>;

    const appStates = this.reducerUtilityService.getApplicationStates();
    const effectiveDate = appStates.EffectiveDate as EffectiveDateStateStructure;

    const selectedYear = effectiveDate.effectiveDateYear;
    try {
      selectedPlan =
        plansFromState.filter(plan => plan.attributes.PLAN_ID === planInfo.planIdForEnrollment)[0];
    } catch (e) {
      console.error('No plan selected');
    }

    const productData = {
      planId: planInfo.planIdForEnrollment,
      planName: selectedPlan && selectedPlan.attributes && selectedPlan.attributes.PRODUCT_NAME,
      cmsPlanID: selectedPlan && selectedPlan.attributes && selectedPlan.attributes.CMS_PLAN_ID,
      pbpId: selectedPlan && selectedPlan.attributes && selectedPlan.attributes.PBP_ID
    };


    const payloadArray = [];
    for (const question of inputArray) {
      let dob = this.reducerUtilService.getPayloadState('personalInfo.dob') as string;
      const prePopulateData: PrePopulatedDataStateStructure = this.reducerUtilService.getState('PrePopulateData');
      if (!dob) {
        dob = prePopulateData.prepopulatedFields.dob;
      }

      const postElectionQuestions = {
        ELECTIONCODE: question.ELECTIONCODE,
        ELECTIONREASON: question.ELECTIONREASON,
        PARTAEFFDATE: this.dateUtilityService.getNullOrFormattedDate(this.reducerUtilService.getPayloadState('enrollment.partAEffectiveDate'), 'MM/DD/YYYY'),
        PARTBEFFDATE: this.dateUtilityService.getNullOrFormattedDate(this.reducerUtilService.getPayloadState('enrollment.partBEffectiveDate'), 'MM/DD/YYYY'),
        BIRTHDATE: this.dateUtilityService.getDateAsFormattedString(dob, 'MM/DD/YYYY'),
        PBP: productData.pbpId,
        PLAN: productData.cmsPlanID,
        /** get this date from form  */
        QUESTIONINSERTDATE: (() => {
          let date = null;
          const categories = this.electionQuestionsForm.controls;
          Object.keys(categories).forEach((catName: string) => {
            const catForm = categories[catName];
            const control = catForm.get(question.QUESTIONNUMBER);
            if (
              control &&
              control.get('date')
            ) {
              let uglyDate = control.get('date').value;
              date = this.dateUtilityService.getDateAsFormattedString(uglyDate, "MM/DD/YYYY");
            }
          });
          return date;
        })(),
        QUESTIONNUMBER: question.QUESTIONNUMBER,
        RECEIVEDDATE: this.dateUtilityService.getTodayAsFormattedString('MM/DD/YYYY'),
        SELECTEDYEAR: selectedYear
      };
      payloadArray.push(postElectionQuestions);
    }
    const postElectionQuestionValidations = await this.validateSelectedElection(payloadArray);

    if (postElectionQuestionValidations instanceof HttpErrorResponse) {
      this.setEffectiveDateOnServiceFailure();
      this.displayDateSelectionSection = true;

    } else if (postElectionQuestionValidations) {
      this.displayDateSelectionSection = true;
      this.setElectionPeriodContent();
    }


    const inputAr: any[] = [];
    const electionInputArrays: ElectionInputStructure[] = [];
    for (const obj of postElectionQuestionValidations.payload.electionOutput) {
      const electionInput: ElectionInputStructure = {
        electionCode: obj.ELECTIONCODE,
        eligibilityPeriodEndDate: obj.ELIGIBILITYPERIODENDDATE,
        eligibilityPeriodStartDate: obj.ELIGIBILITYPERIODSTARTDATE,
        finalIndicator: obj.INDICATOR,
        proposedEffectiveDate: [],
        questionInsertDate: null,
        questionNumber: obj.QUESTIONNUMBER,
        questionValidIndicator: obj.INDICATOR
      };
      const subInputAr: any[] = [];
      let sublabelText = '';
      for (const effectiveDate of obj.EFFDATE) {

        electionInput.proposedEffectiveDate.push(effectiveDate);

        let arrayItem = {
          key: effectiveDate,
          value: effectiveDate + ' - ' + obj.QUESTIONDESCRIPTION,
          label: effectiveDate,
          parentQuesNumber: obj.QUESTIONNUMBER,
          manuallyEnteredDate: null
        };

        try {
          let dateManuallyEntered = null;
          Object.keys(this.electionQuestionsForm.controls).forEach(catKey => {
            let categoryControls: UntypedFormGroup = this.electionQuestionsForm.controls[catKey] as UntypedFormGroup;
            if (
              categoryControls.controls[obj.QUESTIONNUMBER] &&
              categoryControls.controls[obj.QUESTIONNUMBER].hasOwnProperty('controls')
            ) {
              dateManuallyEntered = (categoryControls.controls[obj.QUESTIONNUMBER] as UntypedFormGroup).controls['date'].value
            }
          })
          arrayItem.manuallyEnteredDate = this.dateUtilityService.getDateAsFormattedString(dateManuallyEntered, 'MM/DD/YYYY');

          if (dateManuallyEntered) {
            electionInput.questionInsertDate = this.dateUtilityService.getDateAsFormattedString(dateManuallyEntered, 'MM/DD/YYYY');
          }
        } catch (e) {
          console.error(e);
        }



        subInputAr.push(arrayItem);
      }
      electionInputArrays.push(electionInput);
      if (subInputAr.length === 0) {
        sublabelText = 'No option available for selected date';
      }
      inputAr.push({
        subInputs: subInputAr,
        label: obj.QUESTIONDESCRIPTION,
        subLabel: sublabelText
      });
      this.effectiveDatePossibilities = inputAr;
    }
    // save the election input response to the component state
    this.ngRedux.dispatch(saveElectionAppIn(electionInputArrays));
  }

  validateSelectedElection(inputArr: PlanBasedQuestionsOutput[]): Promise<any> {
    const payload = {
      payload: {
        electionInput: inputArr
      }
    };
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/cmcsnr/validateElection/validateSelectedElection',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        'x-hmhs-test-backend': environment.external.engen.eBill.overrideEnv
      }),
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.WAS_INTER);
    return this.httpClient.makeRequest(requestConfig);
  }

  setElectionPeriodContent() {
    this.enrollmentPeriod = this.commonUtilityService.annualEnrollmentPeriod();
    this.electionPeriodSelectionMessage = "Based on the information you've provided, you can enroll in the following election periods and effective dates. Choose your preferred election and effective date.";
    if (this.enrollmentPeriod) {
      this.electionPeriodSelectionMessage = "Based on the information you've provided, you can enroll in the following election periods and effective dates. Choose your preferred election and effective date. If no reason is selected your effective date will be January 1st of the next year.";
    }
  }

  isFormGroup(control: AbstractControl) {
    if (control instanceof UntypedFormGroup) {
      return true;
    }
  }

  setEffectiveDateOnServiceFailure() {
    const inputArray: any[] = [];
    const subInputArray: any[] = [];
    const questionDescription = "Service failure allows user to continue";

    this.startOfNextMonth = this.dateUtilityService.getFirstOfNextMonth().format('MM/DD/YYYY');
    let electionValue = this.startOfNextMonth + "-" + questionDescription;

    subInputArray.push({
      key: this.startOfNextMonth,
      value: electionValue,
      label: this.startOfNextMonth,
    });

    inputArray.push({
      subInputs: subInputArray,
      label: questionDescription,
      subLabelText: questionDescription
    });

    this.ngRedux.dispatch(saveElectionAppIn(inputArray));

  }

  effectiveDateRadioChange(event: MatRadioChange, data) {
    const selectedDate = this.dateUtilityService.getDateAsMoment(data);
    const selectedDateYear = selectedDate.format('YYYY');

    const appStates = this.reducerUtilityService.getApplicationStates();
    const effectiveDate = appStates.EffectiveDate as EffectiveDateStateStructure;
    const effectiveDateYear = effectiveDate.effectiveDateYear;

    if (selectedDateYear != effectiveDateYear) {
      this.openConflictingEffectiveDateModal(selectedDateYear, effectiveDateYear);
    }
  }

  openConflictingEffectiveDateModal(selectedDateYear, effectiveDateYear) {
    this.effectiveDateRadioForm.reset();
    this.matDialog.open(ConflictingEffectiveDateModalComponent, {
      width: '50vw',
      disableClose: true,
      data: {
        planYear: effectiveDateYear,
        otherYear: selectedDateYear
      }
    });
  }

}